import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';

interface UseProcessEmailsCsvReturn {
    processEmailsCsv: (input: FormData | File, fileKey?: string) => Promise<string[]>;
    emails: string[];
    isLoading: boolean;
    error: string | null;
    clearEmails: () => void;
}

const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length > 0;
};

const parseCsvEmails = (csvText: string): string[] => {
    const lines = csvText.split(/\r?\n/);
    const emails: string[] = [];

    for (const line of lines) {
        if (!line.trim()) continue;
        const columns = line.split(/[,;\t]/);
        const email = columns[0]?.trim();

        if (email && validateEmail(email)) {
            emails.push(email);
        }
    }

    return emails; // Remove duplicates
};

export const useProcessEmailsCsv = (): UseProcessEmailsCsvReturn => {
    const [emails, setEmails] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const processEmailsCsv = useCallback(
        async (input: FormData | File, fileKey: string = 'file'): Promise<string[]> => {
            setIsLoading(true);
            setError(null);

            return new Promise((resolve, reject) => {
                let file: File;

                // Handle different input types
                if (input instanceof FormData) {
                    const extractedFile = input.get(fileKey) as File;
                    if (!extractedFile) {
                        const errorMessage = `No file found with key '${fileKey}' in FormData`;
                        setError(errorMessage);
                        setIsLoading(false);
                        reject(new Error(errorMessage));
                        return;
                    }
                    file = extractedFile;
                } else if (input instanceof File) {
                    file = input;
                } else {
                    const errorMessage = 'Input must be FormData or File';
                    setError(errorMessage);
                    setIsLoading(false);
                    reject(new Error(errorMessage));
                    return;
                }

                // Check if it's a CSV file
                const fileExtension = file.name.split('.').pop()?.toLowerCase();
                if (fileExtension !== 'csv') {
                    const errorMessage = 'File must be a CSV file';
                    setError(errorMessage);
                    setIsLoading(false);
                    reject(new Error(errorMessage));
                    return;
                }

                const reader = new FileReader();

                reader.onload = (e) => {
                    try {
                        const text = e.target?.result as string;
                        const processedEmails = parseCsvEmails(text);

                        if (processedEmails.length === 0) {
                            const errorMessage = 'No valid emails found in the CSV file';
                            setError(errorMessage);
                            setIsLoading(false);
                            reject(new Error(errorMessage));
                            return;
                        }

                        setEmails(processedEmails);
                        setIsLoading(false);
                        resolve(processedEmails);
                    } catch (err) {
                        const errorMessage = 'Failed to process CSV file';
                        setError(errorMessage);
                        setIsLoading(false);
                        reject(new Error(errorMessage));
                    }
                };

                reader.onerror = () => {
                    const errorMessage = 'Failed to read file';
                    setError(errorMessage);
                    setIsLoading(false);
                    reject(new Error(errorMessage));
                };

                reader.readAsText(file);
            });
        },
        []
    );

    const clearEmails = useCallback(() => {
        setEmails([]);
        setError(null);
    }, []);

    return {
        processEmailsCsv,
        emails,
        isLoading,
        error,
        clearEmails
    };
};
